import * as React from "react";
import { User, Palette, Globe, Sun, Moon, Monitor } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useTheme } from "next-themes";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isMultiLocale, SUPPORTED_LOCALES, LOCALE_META } from "../../../translations/config";
import { HomeyConnectionSection } from "./components/HomeyConnectionSection";
import { KioskSettingsSection } from "./components/KioskSettingsSection";
import { DeviceVisibilitySection } from "./components/DeviceVisibilitySection";
import { useAuth } from "@/hooks/useAuth";

/**
 * SettingsPage - Settings
 *
 * Features:
 * - Homey connection section
 * - Kiosk display preferences section
 * - Appearance: theme control (Light / Dark / System) via next-themes
 * - Profile: link to /profile with signed-in user's email
 * - Language preference section (only visible when isMultiLocale is true)
 */
export function SettingsPage() {
  const { t, i18n } = useTranslation("dashboard");
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();

  const handleLanguageChange = (locale: string) => {
    i18n.changeLanguage(locale);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {t("pages.settings.title")}
        </h1>
        <p className="text-base text-muted-foreground md:text-lg">
          {t("pages.settings.description")}
        </p>
      </div>

      <Separator />

      {/* Homey connection */}
      <HomeyConnectionSection />

      {/* Kiosk & display preferences */}
      <KioskSettingsSection />

      {/* Device visibility — hide devices from the Home dashboard */}
      <DeviceVisibilitySection />

      {/* Appearance */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
          Appearance
        </h2>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-muted">
                <Palette className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-foreground">Theme</h3>
                <p className="text-sm text-muted-foreground">
                  Choose light, dark, or follow your system setting
                </p>
              </div>
              <Select value={theme ?? "system"} onValueChange={setTheme}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">
                    <span className="flex items-center gap-2">
                      <Sun className="h-4 w-4" />
                      <span>Light</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="dark">
                    <span className="flex items-center gap-2">
                      <Moon className="h-4 w-4" />
                      <span>Dark</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="system">
                    <span className="flex items-center gap-2">
                      <Monitor className="h-4 w-4" />
                      <span>System</span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Profile */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
          Account
        </h2>
        <Link to="/profile">
          <Card className="group cursor-pointer transition-colors hover:bg-muted/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-muted transition-colors group-hover:border-border">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-foreground">Profile</h3>
                  {user?.email && (
                    <p className="truncate text-sm text-muted-foreground">{user.email}</p>
                  )}
                  {!user?.email && (
                    <p className="text-sm text-muted-foreground">
                      Manage your personal information
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </section>

      {/* Language preference - only shown when multiple locales are supported */}
      {isMultiLocale && (
        <section className="space-y-4">
          <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
            {t("pages.settings.language.title")}
          </h2>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-muted">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-foreground">
                    {t("pages.settings.language.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t("pages.settings.language.description")}
                  </p>
                </div>
                <Select value={i18n.language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LOCALES.map((locale) => {
                      const meta = LOCALE_META[locale];
                      return (
                        <SelectItem key={locale} value={locale}>
                          <span className="flex items-center gap-2">
                            <span>{meta?.flag}</span>
                            <span>{meta?.name ?? locale}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}

export default SettingsPage;
